<script>
import {mapActions} from 'vuex';
import { useAlert } from 'dashboard/composables';

export default {
    props: {
        appointmentId: {
            type: String,
            required: true,
        },
        reminders: {
            type: Array,
            required: true,
        }
    },
    data() {
        return {
            reminderList: [],
            timeUnits: [
                { value: 'minutes', label: 'Minutes prior', multiplier: 1/60 },
                { value: 'hours', label: 'Hours prior', multiplier: 1 },
                { value: 'days', label: 'Days prior', multiplier: 24 }
            ],
        }
    },
    mounted() {
        // Convert hours to minutes for better UX
        if (this.reminders && this.reminders.length > 0) {
            this.reminderList = this.reminders.map(hours => ({
                customValue: hours * 60,
                unit: 'minutes'
            }));
        } else {
            // Set default reminder when no reminders exist
            const defaultReminderValue = localStorage.getItem('defaultReminderValue');
            if (defaultReminderValue) {
                const value = parseInt(defaultReminderValue);
                this.reminderList = [{
                    customValue: value * 60, // Convert hours to minutes
                    unit: 'minutes'
                }];
            } else {
                // If no default in localStorage, set 30 minutes as default
                this.reminderList = [{
                    customValue: 30,
                    unit: 'minutes'
                }];
            }
        }
    },
    methods: {
        ...mapActions('appointments', ['updateAppointmentReminders']),
        addReminder() {
            this.reminderList.push({
                customValue: 30,
                unit: 'minutes'
            });
        },
        removeReminder(index) {
            this.reminderList.splice(index, 1);
        },
        convertToHours(value, unit) {
            const unitConfig = this.timeUnits.find(u => u.value === unit);
            return value * unitConfig.multiplier;
        },
        async handleUpdate() {
            try {
                await this.updateAppointmentReminders({
                    appointmentId: this.appointmentId,
                    reminders: this.reminderList.map(r => this.convertToHours(r.customValue, r.unit))
                });
                useAlert("Reminders updated successfully");
                this.$emit('refreshDetails');
            } catch(err) {
                console.error(err);
                useAlert(err);
            } finally {
                this.$emit('closeModal');
            }
        }
    },
    emits: ['closeModal', 'refreshDetails']
}
</script>

<template>
    <div class="fixed inset-0 flex items-center justify-center h-[100vh] w-[100vw] bg-slate-900/50 dark:bg-slate-800/50 z-50">
        <div class="p-6 border-2 rounded-lg bg-white dark:bg-slate-800 shadow-lg max-w-md w-full mx-auto">
            <div class="mb-4">
                <div class="flex justify-between w-full mb-2">
                    <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Update reminders</span>
                    <woot-button 
                        size="tiny"
                        variant="smooth"
                        @click="addReminder"
                    >Add reminder</woot-button>
                </div>

                <div v-for="(reminder, index) in reminderList" :key="index" 
                    class="flex items-center gap-2 mb-2">
                    <input 
                        type="number"
                        v-model.number="reminder.customValue"
                        min="1"
                        class="w-24 border rounded-md text-gray-900 dark:text-white dark:bg-slate-700 dark:border-gray-600"
                    />
                    <select 
                        v-model="reminder.unit"
                        class="w-32 border rounded-md text-gray-900 dark:text-white dark:bg-slate-700 dark:border-gray-600"
                    >
                        <option v-for="unit in timeUnits" :key="unit.value" :value="unit.value">
                            {{ unit.label }}
                        </option>
                    </select>
                    <button 
                        v-if="reminderList.length > 1"
                        @click="removeReminder(index)"
                        type="button"
                    >
                        <fluent-icon 
                            icon="delete" 
                            class="text-red-500 hover:text-red-700 cursor-pointer mb-4"
                        />
                    </button>
                </div>
            </div>

            <div class="flex gap-2 justify-end">
                <woot-button
                    variant="smooth"
                    color-scheme="secondary"
                    @click="$emit('closeModal')"
                >Cancel</woot-button>
                <woot-button
                    variant="smooth"
                    color-scheme="primary"
                    @click="handleUpdate"
                >Update Reminder</woot-button>
            </div>
        </div>
    </div>
</template>